import { FC } from 'react'
import { Outlet, useNavigate, useOutletContext } from 'react-router-dom'

import { OneDataCollection } from '@factorialco/factorial-one-react/dist/experimental'
import { Add, Download, PersonMinus, PersonPlus } from '@factorialco/factorial-one-react/icons/app'
import {
  ProjectForSelectFragment,
  ProjectPeopleDataCollectionDocument,
  ProjectPeopleDataCollectionItemFragment,
} from 'generated/resources/graphql'
import compact from 'lodash/compact'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useGraphqlDataSource } from 'modules/core/hooks/useGraphqlDataSource'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import buildUrl from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'
import MoneyWithCurrency, { isCurrency } from 'modules/core/lib/money'

import { formatProjectHours } from 'modules/project_management/lib/formatHours'

import { IProjectWorkersPanelData } from '../ProjectEmployeesDataTable/types'

interface ProjectPeopleDataCollectionProps {
  projectCurrency: string
  canAssignEmployees: boolean
  canExportData: boolean
  canSeeLaborCosts: boolean
  canSeeSpendings: boolean
  onClick: (newData: IProjectWorkersPanelData) => void
}

const ProjectPeopleDataCollection: FC<ProjectPeopleDataCollectionProps> = ({
  projectCurrency,
  canAssignEmployees,
  canExportData,
  canSeeLaborCosts,
  canSeeSpendings,
  onClick,
}) => {
  const {
    access: { locale },
  } = useCurrentSession()

  const { navigation } = useNavigation()
  const navigate = useNavigate()

  const { hasExpenses, hasFlexibleTracking, project } = useOutletContext<{
    hasExpenses: boolean
    hasFlexibleTracking: boolean
    project: ProjectForSelectFragment
  }>()

  const itemOnClick = (item: ProjectPeopleDataCollectionItemFragment) => () => {
    onClick({
      projectWorkerId: item.id,
      employeeAccessAvatar: item.employee?.access?.avatar,
      employeeFullName: item.employee?.fullName,
      imputedMinutes: item.inputedMinutes,
      laborCostCents: item.laborCostCents,
      laborCostCurrency: projectCurrency,
    })
  }

  const source = useGraphqlDataSource({
    document: ProjectPeopleDataCollectionDocument,
    mapData: (data) => data.projectManagement.projectWorkersConnection.nodes,
    mapVariables: ({ search, sortings }) => ({
      projectId: project.id,
      employeeName: search,
      includeCost: canSeeLaborCosts,
      includeInputedMinutes: true,
      hasSpendings: canSeeSpendings,
      sortOrder: sortings?.[0],
    }),
    mapPagination: (data) => ({
      total: data.projectManagement.projectWorkersConnection.totalCount ?? 0,
    }),
    itemOnClick,
    itemActions: ({ assigned, employee, id }) => {
      const employeeId = employee?.id
      if (!employeeId) return []

      return [
        assigned
          ? {
              label: i18n.t('project_management.table.project_employees.menu_links.unassign'),
              icon: PersonMinus,
              onClick: () => {
                navigate(
                  navigation.projects.id.details.employees.unassignEmployee.employeeId.projectWorkerId.fullPath(
                    {
                      id: project.id,
                      employeeId,
                      projectWorkerId: id,
                    }
                  )
                )
              },
            }
          : {
              label: i18n.t('project_management.table.project_employees.menu_links.assign'),
              icon: PersonPlus,
              onClick: () => {
                navigate(
                  navigation.projects.id.details.employees.assignEmployee.employeeId.projectWorkerId.fullPath(
                    {
                      id: project.id,
                      employeeId,
                      projectWorkerId: id,
                    }
                  )
                )
              },
            },
      ]
    },
    primaryActions: () =>
      canAssignEmployees
        ? {
            label: i18n.t('project_management.details.assign_employees'),
            icon: Add,
            onClick: () => {
              track(trackingEvents.projectManagement.projectWorker.assignEmployeesToProjectStart, {
                project_id: project.id,
              })

              navigate(
                navigation.projects.id.details.employees.workers.fullPath({
                  id: project.id,
                })
              )
            },
          }
        : undefined,
    secondaryActions: () =>
      canExportData
        ? [
            {
              label: i18n.t('project_management.table.projects_list.table_options.export.title'),
              description: i18n.t(
                'project_management.table.projects_list.table_options.export.description'
              ),
              icon: Download,
              onClick: () => {
                track(trackingEvents.projectManagement.projects.downloadProjectsReportStart)
                navigate(
                  navigation.projects.id.details.employees.export.fullPath({ id: project.id }),
                  { state: { projectForSelect: project } }
                )
              },
            },
          ]
        : undefined,
    search: {
      enabled: true,
    },
    sortings: {
      full_name: {
        label: i18n.t('project_management.table.project_employees.headers.name'),
      },
      assigned: {
        label: i18n.t('project_management.table.project_employees.headers.status'),
      },
    },
    defaultSorting: {
      field: 'assigned',
      order: 'desc',
    },
  })

  return (
    <>
      <OneDataCollection
        source={source}
        visualizations={[
          {
            type: 'table',
            options: {
              columns: compact([
                {
                  label: i18n.t('project_management.table.project_employees.headers.name'),
                  render: ({ employee }: ProjectPeopleDataCollectionItemFragment) => ({
                    type: 'person',
                    value: {
                      firstName: employee?.firstName || '',
                      lastName: employee?.lastName || '',
                      src: employee?.access?.avatar
                        ? buildUrl(employee?.access?.avatar)
                        : undefined,
                    },
                  }),
                  sorting: 'full_name',
                },
                {
                  label: i18n.t('project_management.table.project_employees.headers.hours'),
                  render: ({ inputedMinutes }: ProjectPeopleDataCollectionItemFragment) => ({
                    type: 'text',
                    value: formatProjectHours(inputedMinutes),
                  }),
                },
                canSeeLaborCosts
                  ? {
                      label: i18n.t(
                        'project_management.table.project_employees.headers.labor_cost'
                      ),
                      render: ({ laborCostCents }: ProjectPeopleDataCollectionItemFragment) => ({
                        type: 'text',
                        value:
                          laborCostCents && isCurrency(projectCurrency)
                            ? MoneyWithCurrency({
                                amount: laborCostCents,
                                currency: projectCurrency,
                                locale,
                              })
                            : '-',
                      }),
                    }
                  : null,
                canSeeSpendings
                  ? {
                      label: i18n.t('project_management.table.project_employees.headers.spendings'),
                      render: ({ spendingCostCents }: ProjectPeopleDataCollectionItemFragment) => ({
                        type: 'text',
                        value:
                          spendingCostCents && isCurrency(projectCurrency)
                            ? MoneyWithCurrency({
                                amount: spendingCostCents,
                                currency: projectCurrency,
                                locale,
                              })
                            : '-',
                      }),
                    }
                  : null,
                {
                  label: i18n.t('project_management.table.project_employees.headers.status'),
                  render: ({ assigned }: ProjectPeopleDataCollectionItemFragment) => ({
                    type: 'status',
                    value: {
                      label: assigned
                        ? i18n.t(
                            'project_management.table.project_employees.assignation_tags.assigned'
                          )
                        : i18n.t(
                            'project_management.table.project_employees.assignation_tags.not_assigned'
                          ),
                      status: assigned ? 'info' : 'neutral',
                    },
                  }),
                  sorting: 'assigned',
                },
              ]),
            },
          },
        ]}
      />
      <Outlet context={{ hasExpenses, hasFlexibleTracking }} />
    </>
  )
}

export default ProjectPeopleDataCollection
